import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import activityTrackingService from '@/services/LearnerService/ActivityTrackingService';

// Configuration constants
const CONFIG = {
  HEARTBEAT_INTERVAL: 5 * 60 * 1000,      // 5 minutes in milliseconds
  IDLE_TIMEOUT: 5 * 60 * 1000,            // 5 minutes of inactivity
  ACTIVITY_EVENTS: ['mousedown', 'keydown', 'scroll', 'touchstart'] as const,
  DEBUG: process.env.NODE_ENV === 'development',
} as const;

interface ActivityTrackingContextValue {
  isTracking: boolean;
  lastActivityTime: number | null;
}

const ActivityTrackingContext = createContext<ActivityTrackingContextValue | undefined>(undefined);

export const ActivityTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();

  // Refs for timers and state (avoid re-renders)
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const idleCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityTimeRef = useRef<number>(Date.now());
  const isTrackingRef = useRef<boolean>(false);
  const isPageVisibleRef = useRef<boolean>(!document.hidden);

  // Track component mount state
  const mountedRef = useRef<boolean>(false);
  const cleanupRef = useRef<boolean>(false);

  // State for context consumers (triggers re-renders)
  const [isTracking, setIsTracking] = useState<boolean>(false);
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());

  // Set up activity listeners
  useEffect(() => {
    // Debug: Effect triggered
    if (CONFIG.DEBUG) {
      console.log('[ActivityTracking] Effect triggered', {
        isAuthenticated,
        user: user?.email,
        timestamp: new Date().toISOString()
      });
    }

    // Reset cleanup flag
    cleanupRef.current = false;

    // Early return if not authenticated
    if (!isAuthenticated || !user) {
      if (CONFIG.DEBUG) {
        console.log('[ActivityTracking] Not authenticated, skipping setup');
      }
      mountedRef.current = true;
      return;
    }

    if (CONFIG.DEBUG) {
      console.log('[ActivityTracking] Setting up activity tracking');
    }

    mountedRef.current = true;

    // === ALL FUNCTIONS DEFINED INSIDE EFFECT (no useCallback) ===

    const logActivity = async () => {
      if (cleanupRef.current) return;
      if (CONFIG.DEBUG) console.log('[ActivityTracking] Logging activity...');

      try {
        const response = await activityTrackingService.logActivity();
        if (CONFIG.DEBUG && response.success) {
          console.log('[ActivityTracking] Activity logged successfully');
        }
      } catch (error) {
        if (CONFIG.DEBUG) console.error('[ActivityTracking] Error:', error);
      }
    };

    const startHeartbeat = () => {
      if (heartbeatTimerRef.current) return;
      if (CONFIG.DEBUG) console.log('[ActivityTracking] Starting heartbeat');

      logActivity(); // Log immediately

      heartbeatTimerRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTimeRef.current;

        if (timeSinceLastActivity < CONFIG.IDLE_TIMEOUT && isPageVisibleRef.current) {
          logActivity();
        }
      }, CONFIG.HEARTBEAT_INTERVAL);

      isTrackingRef.current = true;
      setIsTracking(true);
    };

    const stopHeartbeat = () => {
      if (CONFIG.DEBUG) console.log('[ActivityTracking] Stopping heartbeat');
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      isTrackingRef.current = false;
      setIsTracking(false);
    };

    const handleUserActivity = () => {
      const now = Date.now();
      lastActivityTimeRef.current = now;
      setLastActivityTime(now);

      if (!isTrackingRef.current && isPageVisibleRef.current) {
        if (CONFIG.DEBUG) console.log('[ActivityTracking] User active - restarting');
        startHeartbeat();
      }
    };

    const checkIdleStatus = () => {
      if (cleanupRef.current) return;
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityTimeRef.current;

      if (timeSinceLastActivity >= CONFIG.IDLE_TIMEOUT && isTrackingRef.current) {
        if (CONFIG.DEBUG) console.log('[ActivityTracking] User idle');
        stopHeartbeat();
      }
    };

    const handleVisibilityChange = () => {
      if (cleanupRef.current) return;
      isPageVisibleRef.current = !document.hidden;

      if (CONFIG.DEBUG) {
        console.log('[ActivityTracking] Visibility:', isPageVisibleRef.current ? 'visible' : 'hidden');
      }

      if (document.hidden) {
        stopHeartbeat();
      } else {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTimeRef.current;
        if (timeSinceLastActivity < CONFIG.IDLE_TIMEOUT) {
          startHeartbeat();
        }
      }
    };

    // === Setup ===
    CONFIG.ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    idleCheckTimerRef.current = setInterval(checkIdleStatus, 60 * 1000);

    startHeartbeat();

    // === Cleanup ===
    return () => {
      if (CONFIG.DEBUG) console.log('[ActivityTracking] Cleaning up');
      cleanupRef.current = true;

      CONFIG.ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (idleCheckTimerRef.current) {
        clearInterval(idleCheckTimerRef.current);
        idleCheckTimerRef.current = null;
      }
      stopHeartbeat();
    };
  }, [isAuthenticated, user]); // ONLY these dependencies!

  const value: ActivityTrackingContextValue = {
    isTracking,
    lastActivityTime,
  };

  return (
    <ActivityTrackingContext.Provider value={value}>
      {children}
    </ActivityTrackingContext.Provider>
  );
};

// Custom hook for accessing context
export const useActivityTrackingContext = () => {
  const context = useContext(ActivityTrackingContext);
  if (context === undefined) {
    throw new Error('useActivityTrackingContext must be used within ActivityTrackingProvider');
  }
  return context;
};
